import {
    IonAvatar,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonModal,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    useFantasyModalBackGuard
} from "./useFantasyModalBackGuard";

import {
    americanFootballOutline,
    closeOutline,
    footballOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import Cookies from "js-cookie";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Player as PlayerModel,
    PlayerStatistics,
    Team,
    getGameDetail,
    getGames,
    getPlayerDetail,
    getPlayersList,
    getTeams
} from "../services/mexfbaApi";

import {
    PlayerGame,
    calculateStats,
    getAverage,
    getPassingRating,
    getPlays,
    playerAppearsInPlay
} from "../pages/Player";



import "./FantasyPlayerModal.css";

interface FantasyPlayerModalProps {
    leagueId: string;
    playerId: string;
    isOpen: boolean;

    onDismiss: () => void;

    onClaimPlayer?: (
        playerId: string
    ) => void | Promise<void>;

    onDropPlayer?: (
        playerId: string
    ) => void | Promise<void>;

    onRequestTrade?: (
        player: {
            id: string;
            name: string;
            position: string;
            realTeamName: string;
            ownerName: string;
        }
    ) => void | Promise<void>;
}

interface FantasyGameRawStats {
    sacks?: number;
    interceptions?: number;
    forcedFumbles?: number;
    fumbleRecoveries?: number;
    safeties?: number;
    defensiveTds?: number;
    returnTds?: number;
    pointsAllowed?: number;
    points?: number;
}

interface FantasyPlayerGame {
    id: string;
    round: number;
    date: string;
    status: string;
    homeAway: "VS" | "EN";
    opponent: string;
    fantasyPoints: number;
    fantasyStats: FantasyGameRawStats;
}

interface FantasyPlayerProjection {
    id: string;
    round: number;
    date: string;
    status: string;
    homeAway: "VS" | "EN";
    opponent: string;
    projection: number;
}

interface FantasyPlayerResponse {
    success: boolean;
    message?: string;

    player: {
        id: string;
        name: string;
        positionId: string;
        position: string;
        realTeamId: string;
        realTeamName: string;
        fantasyStatus: "available" | "owned";
        ownerTeamId: string;
        ownerName: string;
    };

    actions: {
        type: "claim" | "drop" | "trade" | null;
        enabled: boolean;
        label: string;
        reason?: string;
        isMine: boolean;
        isLocked: boolean;
        waiverMode: "waivers" | "free_agents";
    };

    fantasyStats: {
        gamesPlayed: number;
        totalPoints: number;
        averagePoints: number;
    };

    games: FantasyPlayerGame[];

    projections: FantasyPlayerProjection[];
}

interface StatisticRow {
    label: string;
    value: string | number;
}

interface StatsBlock {
    title: string;
    rows: StatisticRow[];
}

function createEmptyStats(): PlayerStatistics {
    return {
        passingAttempts: 0,
        passingCompletions: 0,
        passingYards: 0,
        passingTouchdowns: 0,
        interceptionsThrown: 0,

        rushingAttempts: 0,
        rushingYards: 0,
        rushingTouchdowns: 0,

        receptions: 0,
        receivingYards: 0,
        receivingTouchdowns: 0,

        soloTackles: 0,
        assistedTackles: 0,
        sacks: 0,
        assistedSacks: 0,
        interceptions: 0,
        forcedFumbles: 0,
        recoveredFumbles: 0,
        defensiveTouchdowns: 0,
        passDeflections: 0,

        fieldGoalsMade: 0,
        fieldGoalsAttempted: 0,
        extraPointsMade: 0,
        extraPointsAttempted: 0,
        longestFieldGoal: 0,

        gamesPlayed: 0
    };
}

function getPlayerImageUrl(image?: string): string {
    if (!image) {
        return "";
    }

    if (
        image.startsWith("http://") ||
        image.startsWith("https://") ||
        image.startsWith("data:")
    ) {
        return image;
    }

    return `${process.env.PUBLIC_URL || ""}/${image.replace(
        /^(\.\.\/)+/,
        ""
    )}`;
}

function getPositionShortName(position?: string): string {
    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "4": "DL",
        "5": "LB",
        "6": "DB",
        "7": "K",

        /*
            La posición 8 de Fantasy representa una defensa,
            no una línea ofensiva individual.
        */
        "8": "DEF"
    };

    return positions[position || ""] || "—";
}
interface PlayerWithLegacyPosition extends PlayerModel {
    pos?: string;
}

interface TeamWithFantasyData extends Team {
    logo?: string;
    comp_id?: string;
    color?: string;
}


function getPlayerPosition(player: PlayerModel): string {
    const legacyPlayer = player as PlayerWithLegacyPosition;

    return legacyPlayer.posicion || legacyPlayer.pos || "";
}

function formatDate(date?: string): string {
    if (!date) {
        return "Fecha por confirmar";
    }

    const normalizedDate = date.replace(" ", "T");

    const parsedDate = new Date(
        normalizedDate.length === 10
            ? `${normalizedDate}T12:00:00`
            : normalizedDate
    );

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return new Intl.DateTimeFormat("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric"
    }).format(parsedDate);
}

function getStatBlocks(
    player: PlayerModel,
    stats: PlayerStatistics
): StatsBlock[] {
    const position = getPlayerPosition(player);


    if (position === "1") {
        return [
            {
                title: "Pase",
                rows: [
                    {
                        label: "Intentos",
                        value: stats.passingAttempts
                    },
                    {
                        label: "Completos",
                        value: stats.passingCompletions
                    },
                    {
                        label: "Porcentaje",
                        value: `${getAverage(
                            stats.passingCompletions * 100,
                            stats.passingAttempts
                        )}%`
                    },
                    {
                        label: "Yardas",
                        value: stats.passingYards
                    },
                    {
                        label: "Yardas por intento",
                        value: getAverage(
                            stats.passingYards,
                            stats.passingAttempts
                        )
                    },
                    {
                        label: "Touchdowns",
                        value: stats.passingTouchdowns
                    },
                    {
                        label: "Intercepciones",
                        value: stats.interceptionsThrown
                    },
                    {
                        label: "QBR",
                        value: getPassingRating(stats)
                    }
                ]
            }
        ];
    }

    if (position === "2") {
        return [
            {
                title: "Carrera",
                rows: [
                    {
                        label: "Acarreos",
                        value: stats.rushingAttempts
                    },
                    {
                        label: "Yardas",
                        value: stats.rushingYards
                    },
                    {
                        label: "Promedio",
                        value: getAverage(
                            stats.rushingYards,
                            stats.rushingAttempts
                        )
                    },
                    {
                        label: "Touchdowns",
                        value: stats.rushingTouchdowns
                    }
                ]
            },
            {
                title: "Recepciones",
                rows: [
                    {
                        label: "Recepciones",
                        value: stats.receptions
                    },
                    {
                        label: "Yardas",
                        value: stats.receivingYards
                    },
                    {
                        label: "Promedio",
                        value: getAverage(
                            stats.receivingYards,
                            stats.receptions
                        )
                    },
                    {
                        label: "Touchdowns",
                        value: stats.receivingTouchdowns
                    }
                ]
            }
        ];
    }

    if (position === "3") {
        return [
            {
                title: "Producción terrestre",
                rows: [
                    {
                        label: "Acarreos",
                        value: stats.rushingAttempts
                    },
                    {
                        label: "Yardas",
                        value: stats.rushingYards
                    },
                    {
                        label: "Promedio",
                        value: getAverage(
                            stats.rushingYards,
                            stats.rushingAttempts
                        )
                    },
                    {
                        label: "Touchdowns",
                        value: stats.rushingTouchdowns
                    }
                ]
            },
            {
                title: "Recepciones",
                rows: [
                    {
                        label: "Recepciones",
                        value: stats.receptions
                    },
                    {
                        label: "Yardas",
                        value: stats.receivingYards
                    },
                    {
                        label: "Promedio",
                        value: getAverage(
                            stats.receivingYards,
                            stats.receptions
                        )
                    },
                    {
                        label: "Touchdowns",
                        value: stats.receivingTouchdowns
                    }
                ]
            }
        ];
    }

    if (
        position === "4" ||
        position === "5" ||
        position === "6"
    ) {
        return [
            {
                title: "Defensiva",
                rows: [
                    {
                        label: "Tacleadas solitarias",
                        value: stats.soloTackles
                    },
                    {
                        label: "Tacleadas asistidas",
                        value: stats.assistedTackles
                    },
                    {
                        label: "Tacleadas totales",
                        value:
                            stats.soloTackles +
                            stats.assistedTackles
                    },
                    {
                        label: "Sacks",
                        value: (
                            stats.sacks +
                            stats.assistedSacks / 2
                        ).toFixed(1)
                    },
                    {
                        label: "Intercepciones",
                        value: stats.interceptions
                    },
                    {
                        label: "Fumbles forzados",
                        value: stats.forcedFumbles
                    },
                    {
                        label: "Fumbles recuperados",
                        value: stats.recoveredFumbles
                    },
                    {
                        label: "Pases defendidos",
                        value: stats.passDeflections
                    },
                    {
                        label: "Touchdowns defensivos",
                        value: stats.defensiveTouchdowns
                    }
                ]
            }
        ];
    }

    if (position === "7") {
        return [
            {
                title: "Patadas",
                rows: [
                    {
                        label: "Goles de campo",
                        value: `${stats.fieldGoalsMade}/${stats.fieldGoalsAttempted}`
                    },
                    {
                        label: "Puntos extra",
                        value: `${stats.extraPointsMade}/${stats.extraPointsAttempted}`
                    },
                    {
                        label: "Gol de campo más largo",
                        value: stats.longestFieldGoal
                    }
                ]
            }
        ];
    }

    return [];
}

function getDefenseBlocks(
    games: FantasyPlayerGame[]
): StatsBlock[] {
    const total = games.reduce(
        function (
            result: Required<FantasyGameRawStats>,
            game
        ) {
            const stats = game.fantasyStats || {};

            result.sacks += Number(stats.sacks || 0);
            result.interceptions += Number(
                stats.interceptions || 0
            );

            result.forcedFumbles += Number(
                stats.forcedFumbles || 0
            );

            result.fumbleRecoveries += Number(
                stats.fumbleRecoveries || 0
            );

            result.safeties += Number(
                stats.safeties || 0
            );

            result.defensiveTds += Number(
                stats.defensiveTds || 0
            );

            result.returnTds += Number(
                stats.returnTds || 0
            );

            result.pointsAllowed += Number(
                stats.pointsAllowed || 0
            );

            result.points += Number(stats.points || 0);

            return result;
        },
        {
            sacks: 0,
            interceptions: 0,
            forcedFumbles: 0,
            fumbleRecoveries: 0,
            safeties: 0,
            defensiveTds: 0,
            returnTds: 0,
            pointsAllowed: 0,
            points: 0
        }
    );

    return [
        {
            title: "Defensa de equipo",
            rows: [
                {
                    label: "Sacks",
                    value: total.sacks
                },
                {
                    label: "Intercepciones",
                    value: total.interceptions
                },
                {
                    label: "Fumbles forzados",
                    value: total.forcedFumbles
                },
                {
                    label: "Fumbles recuperados",
                    value: total.fumbleRecoveries
                },
                {
                    label: "Safeties",
                    value: total.safeties
                },
                {
                    label: "Touchdowns defensivos",
                    value: total.defensiveTds
                },
                {
                    label: "Touchdowns de retorno",
                    value: total.returnTds
                },
                {
                    label: "Puntos permitidos",
                    value: total.pointsAllowed
                }
            ]
        }
    ];
}

function getGameSummary(
    player: PlayerModel,
    stats: PlayerStatistics,
    fantasyGame?: FantasyPlayerGame
): string {
    const position = getPlayerPosition(player);


    if (position === "8") {
        const defenseStats = fantasyGame?.fantasyStats || {};

        return `${Number(
            defenseStats.sacks || 0
        )} SACK · ${Number(
            defenseStats.interceptions || 0
        )} INT · ${Number(
            defenseStats.pointsAllowed || 0
        )} PA`;
    }

    if (position === "1") {
        return `${stats.passingCompletions}/${stats.passingAttempts} CMP · ${stats.passingYards} YDS · ${stats.passingTouchdowns} TD`;
    }

    if (position === "2") {
        return `${stats.rushingAttempts} ATT · ${stats.rushingYards} YDS · ${stats.rushingTouchdowns} TD`;
    }

    if (position === "3") {
        return `${stats.receptions} REC · ${stats.receivingYards} YDS · ${stats.receivingTouchdowns} TD`;
    }

    if (
        position === "4" ||
        position === "5" ||
        position === "6"
    ) {
        return `${stats.soloTackles + stats.assistedTackles} TKL · ${(
            stats.sacks +
            stats.assistedSacks / 2
        ).toFixed(1)} SACK · ${stats.interceptions} INT`;
    }

    if (position === "7") {
        return `${stats.fieldGoalsMade}/${stats.fieldGoalsAttempted} FG · ${stats.extraPointsMade}/${stats.extraPointsAttempted} XP`;
    }

    return `${stats.gamesPlayed} PJ`;
}

const FantasyPlayerModal: React.FC<
    FantasyPlayerModalProps
> = function ({
    isOpen,
    leagueId,
    playerId,
    onDismiss,
    onClaimPlayer,
    onDropPlayer,
    onRequestTrade
}) {


    const [player, setPlayer] =
        useState<PlayerModel | null>(null);

    const [team, setTeam] =
        useState<Team | null>(null);

    const [regularStats, setRegularStats] =
        useState<PlayerStatistics>(createEmptyStats());

    const [playerGames, setPlayerGames] = useState<
        PlayerGame[]
    >([]);

    const [fantasyData, setFantasyData] =
        useState<FantasyPlayerResponse | null>(null);

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const requestVersionRef = useRef(0);

    const loadPlayer = useCallback(
        async function (forceRefresh = false) {
            if (!leagueId || !playerId) {
                return;
            }

            const requestVersion =
                requestVersionRef.current + 1;

            requestVersionRef.current = requestVersion;

            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const accessToken =
                    Cookies.get("access_token") || "";

                const accessUsername =
                    Cookies.get("access_username") || "";

                const fantasyUrl =
                    "https://fantasy.ftblscore.com/create_league/" +
                    "control_FantasyPlayerState.php" +
                    `?id=${encodeURIComponent(leagueId)}` +
                    `&playerId=${encodeURIComponent(playerId)}` +
                    `&t=${Date.now()}`;

                const [
                    players,
                    teams,
                    games,
                    fantasyResponse
                ] = await Promise.all([
                    getPlayersList(forceRefresh),
                    getTeams(forceRefresh),
                    getGames(forceRefresh),
                    fetch(fantasyUrl, {
                        method: "GET",
                        credentials: "include",
                        cache: "no-store",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Token": accessToken,
                            "X-Access-Username": accessUsername
                        }
                    })
                ]);

                const fantasyResult =
                    await fantasyResponse.json();

                if (
                    !fantasyResponse.ok ||
                    !fantasyResult.success
                ) {
                    throw new Error(
                        fantasyResult.message ||
                            "No fue posible cargar los datos Fantasy."
                    );
                }

                const isDefense =
    fantasyResult.player.positionId === "8";

let selectedPlayer: PlayerModel;

if (isDefense) {
    /*
        DEF no existe en jugadores.json. Se representa con
        el equipo real obtenido de equipos.json/getTeams().
    */
    const defenseTeam = teams.find(
        function (currentTeam) {
            return (
                currentTeam.id ===
                fantasyResult.player.realTeamId
            );
        }
    ) as TeamWithFantasyData | undefined;

    if (!defenseTeam) {
        throw new Error(
            "No fue posible encontrar el equipo de la defensa en el catálogo de equipos."
        );
    }

    selectedPlayer = {
        id: fantasyResult.player.id,

        nombre:
            fantasyResult.player.name ||
            defenseTeam.nombre ||
            "Defensa",

        /*
            La posición 8 identifica una DEF Fantasy.
        */
        posicion: "8",

        equipo_id: defenseTeam.id,

        comp_id: defenseTeam.comp_id || "",

        img:
            getPlayerImageUrl(defenseTeam.logo) ||
            undefined,

        numero: ""
    } as PlayerModel;
} else {
    /*
        Los jugadores individuales sí existen en jugadores.json.
    */
    const basePlayer = players.find(
        function (currentPlayer) {
            return currentPlayer.id === playerId;
        }
    );

    if (!basePlayer) {
        throw new Error(
            "Jugador no encontrado en el catálogo."
        );
    }

    const detail = await getPlayerDetail(
        playerId,
        forceRefresh
    );

    if (
        requestVersion !==
        requestVersionRef.current
    ) {
        return;
    }

    const detailPlayer =
        detail.Info as PlayerModel & {
            birthday?: string;
        };

    selectedPlayer = {
        ...basePlayer,
        ...detailPlayer,

        nacimiento:
            detailPlayer.nacimiento ||
            detailPlayer.birthday ||
            basePlayer.nacimiento,

        img:
            getPlayerImageUrl(detailPlayer.img) ||
            getPlayerImageUrl(basePlayer.img) ||
            undefined
    };
}

const fantasyGameIds = new Set(
    fantasyResult.games.map(function (
        fantasyGame: FantasyPlayerGame
    ) {
        return fantasyGame.id;
    })
);

/*
    DEF usa directamente los partidos y estadísticas que entrega
    control_FantasyPlayerState.php; no necesita descargar detalles.
*/
const relevantGames = isDefense
    ? []
    : games.filter(function (game) {
        return (
            (
                !selectedPlayer.comp_id ||
                game.id_comp === selectedPlayer.comp_id
            ) &&
            fantasyGameIds.has(game.id) &&
            (
                game.partido === "grupos" ||
                !game.partido
            )
        );
    });

const gameDetails = await Promise.all(
    relevantGames.map(async function (game) {
        try {
            const detail = await getGameDetail(
                game.id,
                forceRefresh
            );

            return {
                game,
                detail
            };
        } catch {
            return null;
        }
    })
);

if (
    requestVersion !==
    requestVersionRef.current
) {
    return;
}

const gamesWithPlayer = gameDetails
    .filter(function (
        item
    ): item is PlayerGame {
        return item !== null;
    })
    .filter(function (item) {
        return getPlays(item.detail).some(
            function (play) {
                return playerAppearsInPlay(
                    play,
                    selectedPlayer.id
                );
            }
        );
    });


                const sortedGames = [...gamesWithPlayer].sort(
                    function (first, second) {
                        return (
                            new Date(
                                second.game.fecha || 0
                            ).getTime() -
                            new Date(
                                first.game.fecha || 0
                            ).getTime()
                        );
                    }
                );

                setPlayer(selectedPlayer);

                setTeam(
                    teams.find(function (currentTeam) {
                        return (
                            currentTeam.id ===
                            selectedPlayer.equipo_id
                        );
                    }) || null
                );

                setRegularStats(
                    calculateStats(
                        sortedGames,
                        selectedPlayer
                    )
                );

                setPlayerGames(sortedGames);

                setFantasyData(fantasyResult);
            } catch (requestError) {
                if (
                    requestVersion !==
                    requestVersionRef.current
                ) {
                    return;
                }

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar la ficha Fantasy."
                );
            } finally {
                if (
                    requestVersion ===
                    requestVersionRef.current
                ) {
                    setLoading(false);
                    setRefreshing(false);
                }
            }
        },
        [leagueId, playerId]
    );

    useEffect(
        function () {
            if (!isOpen || !playerId) {
                return;
            }

            setPlayer(null);
            setTeam(null);
            setFantasyData(null);
            setRegularStats(createEmptyStats());
            setPlayerGames([]);
            setError("");

            loadPlayer();
        },
        [isOpen, playerId, loadPlayer]
    );

    const regularBlocks = useMemo(
        function () {
            if (!player || !fantasyData) {
                return [];
            }

            const position = getPlayerPosition(player);


            if (position === "8") {
                return getDefenseBlocks(fantasyData.games);
            }

            return getStatBlocks(player, regularStats);
        },
        [player, regularStats, fantasyData]
    );

    const fantasyGamesById = useMemo(
        function () {
            const result = new Map<string, FantasyPlayerGame>();

            fantasyData?.games.forEach(function (game) {
                result.set(game.id, game);
            });

            return result;
        },
        [fantasyData]
    );

    const displayedGames = useMemo(
        function () {
            if (!fantasyData || !player) {
                return [];
            }

            const position = getPlayerPosition(player);


            /*
                DEF obtiene todos los juegos desde el endpoint.
                Los demás usan los detalles reales ya cargados
                para calcular su resumen estadístico.
            */
            if (position === "8") {
                return fantasyData.games.map(function (fantasyGame) {
                    return {
                        fantasyGame,
                        playerGame: null
                    };
                });
            }

            return playerGames
                .map(function (playerGame) {
                    return {
                        playerGame,
                        fantasyGame: fantasyGamesById.get(
                            playerGame.game.id
                        )
                    };
                })
                .filter(function (item) {
                    return Boolean(item.fantasyGame);
                });
        },
        [
            player,
            playerGames,
            fantasyData,
            fantasyGamesById
        ]
    );

    const seasonProjections = useMemo(
    function () {
        return [...(fantasyData?.projections || [])].sort(
            function (first, second) {
                if (first.round !== second.round) {
                    return first.round - second.round;
                }

                return new Date(first.date || 0).getTime() -
                    new Date(second.date || 0).getTime();
            }
        );
    },
    [fantasyData]
);

async function handleFantasyAction(): Promise<void> {
    if (!fantasyData) {
        return;
    }

    const action = fantasyData.actions;

    if (!action.enabled || !action.type) {
        return;
    }

    if (action.type === "claim") {
        if (!onClaimPlayer) {
            return;
        }

        await onClaimPlayer(
            fantasyData.player.id
        );

        return;
    }

    if (action.type === "drop") {
        if (!onDropPlayer) {
            return;
        }

        await onDropPlayer(
            fantasyData.player.id
        );

        return;
    }

    if (action.type === "trade") {
        if (!onRequestTrade) {
            return;
        }

        await onRequestTrade({
            id: fantasyData.player.id,
            name: fantasyData.player.name,
            position: fantasyData.player.position,
            realTeamName:
                fantasyData.player.realTeamName,
            ownerName:
                fantasyData.player.ownerName
        });
    }
}

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadPlayer(true);

        event.detail.complete();
    }

function handleDismiss(): void {
    requestVersionRef.current += 1;
    onDismiss();
}

useFantasyModalBackGuard({
    isOpen,
    onDismiss: handleDismiss,
    priority: 20000
});

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={handleDismiss}
            className="fantasy-player-modal"
        >
            <IonHeader translucent>
                <IonToolbar className="fantasy-player-modal__toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Cerrar ficha del jugador"
                            onClick={handleDismiss}
                        >
                            <IonIcon icon={closeOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugador</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar jugador"
                            disabled={loading || refreshing}
                            onClick={function () {
                                loadPlayer(true);
                            }}
                        >
                            {refreshing ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <IonIcon icon={refreshOutline} />
                            )}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                className="fantasy-player-modal__content"
            >
                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando jugador..."
                    />
                </IonRefresher>

                {loading && !player ? (
                    <IonProgressBar type="indeterminate" />
                ) : null}

                {error ? (
                    <div className="fantasy-player-modal__empty">
                        <IonIcon icon={statsChartOutline} />

                        <strong>
                            No fue posible cargar al jugador
                        </strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadPlayer(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                ) : null}

                {!loading && !error && player && fantasyData ? (
                    <>
                        <section
                            className="fantasy-player-modal__hero"
                            style={{
                                background: `linear-gradient(
                                    135deg,
                                    ${team?.color || "#102b4d"},
                                    #173e70
                                )`
                            }}
                        >
                            <IonAvatar>
                                {player.img ? (
                                    <img
                                        src={player.img}
                                        alt={`Foto de ${player.nombre}`}
                                    />
                                ) : (
                                    <span>
                                        {player.nombre.charAt(0)}
                                    </span>
                                )}
                            </IonAvatar>

                            <div>
                                <span>
                                    {getPositionShortName(
    getPlayerPosition(player)
)}
{" "}
                                    · #{player.numero || player.num || "—"}
                                </span>

                                <h1>{player.nombre}</h1>

                                <p>
                                    {team?.nombre ||
                                        fantasyData.player.realTeamName ||
                                        "Equipo no disponible"}
                                </p>
                            </div>
                        </section>

                        <main className="fantasy-player-modal__page">
 <section className="fantasy-player-modal__section">
    <div className="fantasy-player-modal__heading">
        <div>
            <span>MOVIMIENTOS</span>
            <h2>Acciones Fantasy</h2>
        </div>
    </div>

    {fantasyData.actions.type ? (
        <>
            <IonButton
                expand="block"
                color={
                    fantasyData.actions.type === "drop"
                        ? "danger"
                        : "primary"
                }
                disabled={
                    !fantasyData.actions.enabled
                }
                onClick={function () {
                    void handleFantasyAction();
                }}
            >
                {fantasyData.actions.label}
            </IonButton>

            {fantasyData.actions.reason && (
                <p className="fantasy-player-modal__action-reason">
                    {fantasyData.actions.reason}
                </p>
            )}
        </>
    ) : (
        <p className="fantasy-player-modal__action-reason">
            No hay movimientos disponibles para este jugador.
        </p>
    )}
</section>


                            <section className="fantasy-player-modal__section">
                                <div className="fantasy-player-modal__heading">
                                    <div>
                                        <span>
                                            ESTADÍSTICAS ACUMULADAS
                                        </span>

                                        <h2>Temporada regular</h2>
                                    </div>

                                    <IonBadge>
                                        {fantasyData.fantasyStats
                                            .gamesPlayed}{" "}
                                        PJ
                                    </IonBadge>
                                </div>

                                {regularBlocks.map(function (block) {
                                    return (
                                        <section
                                            className="fantasy-player-modal__stats-card"
                                            key={block.title}
                                        >
                                            <h3>{block.title}</h3>

                                            <div className="fantasy-player-modal__stats-grid">
                                                {block.rows.map(
                                                    function (row) {
                                                        return (
                                                            <div
                                                                key={
                                                                    row.label
                                                                }
                                                            >
                                                                <strong>
                                                                    {
                                                                        row.value
                                                                    }
                                                                </strong>

                                                                <span>
                                                                    {
                                                                        row.label
                                                                    }
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </section>
                                    );
                                })}
                            </section>

                            <section className="fantasy-player-modal__section">
                                <div className="fantasy-player-modal__heading">
                                    <div>
                                        <span>RENDIMIENTO FANTASY</span>
                                        <h2>Puntos de temporada</h2>
                                    </div>

                                    <IonIcon icon={footballOutline} />
                                </div>

                                <div className="fantasy-player-modal__fantasy-summary">
                                    <div>
                                        <span>PTS TOTALES</span>

                                        <strong>
                                            {fantasyData.fantasyStats.totalPoints.toFixed(
                                                2
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>AVG POR JUEGO</span>

                                        <strong>
                                            {fantasyData.fantasyStats.averagePoints.toFixed(
                                                2
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>PARTIDOS</span>

                                        <strong>
                                            {
                                                fantasyData.fantasyStats
                                                    .gamesPlayed
                                            }
                                        </strong>
                                    </div>
                                </div>
                            </section>
<section className="fantasy-player-modal__section">
    <div className="fantasy-player-modal__heading">
        <div>
            <span>VISTA PREVIA DE TEMPORADA</span>
            <h2>Proyecciones de cada partido</h2>
        </div>

        <IonIcon icon={statsChartOutline} />
    </div>

    {seasonProjections.length === 0 ? (
        <div className="fantasy-player-modal__no-games">
            <IonIcon icon={americanFootballOutline} />

            <strong>Sin calendario disponible</strong>

            <span>
                Aún no hay partidos de temporada regular para
                mostrar proyecciones.
            </span>
        </div>
    ) : (
        <div
            className="fantasy-player-modal__projections-scroll"
            role="list"
            aria-label="Proyecciones por jornada"
        >
            {seasonProjections.map(function (projection) {
                return (
                    <article
                        className="fantasy-player-modal__projection-card"
                        key={projection.id}
                        role="listitem"
                    >
                        <span className="fantasy-player-modal__projection-round">
                            J{projection.round || "—"}
                        </span>

                        <span className="fantasy-player-modal__projection-location">
                            {projection.homeAway}
                        </span>

                        <strong
                            className="fantasy-player-modal__projection-opponent"
                            title={projection.opponent}
                        >
                            {projection.opponent}
                        </strong>

                        <div className="fantasy-player-modal__projection-points">
                            <span>PROYECCIÓN</span>

                            <strong>
                                {projection.projection.toFixed(2)}
                            </strong>

                            <small>PTS</small>
                        </div>
                    </article>
                );
            })}
        </div>
    )}
</section>

                            <section className="fantasy-player-modal__section">
                                <div className="fantasy-player-modal__heading">
                                    <div>
                                        <span>DESGLOSE COMPLETO</span>
                                        <h2>Partido por partido</h2>
                                    </div>

                                    <IonIcon icon={statsChartOutline} />
                                </div>

                                {displayedGames.length === 0 ? (
                                    <div className="fantasy-player-modal__no-games">
                                        <IonIcon
                                            icon={
                                                americanFootballOutline
                                            }
                                        />

                                        <strong>
                                            Sin actividad registrada
                                        </strong>

                                        <span>
                                            Aún no hay puntos Fantasy
                                            registrados para este jugador.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="fantasy-player-modal__games">
                                        {displayedGames.map(
                                            function (item) {
                                                const fantasyGame =
                                                    item.fantasyGame;

                                                if (!fantasyGame) {
                                                    return null;
                                                }

                                                const gameStats =
                                                    item.playerGame
                                                        ? calculateStats(
                                                            [
                                                                item.playerGame
                                                            ],
                                                            player
                                                        )
                                                        : createEmptyStats();

                                                return (
                                                    <article
                                                        className="fantasy-player-modal__game"
                                                        key={
                                                            fantasyGame.id
                                                        }
                                                    >
                                                        <div className="fantasy-player-modal__game-main">
                                                            <div>
                                                                <span>
                                                                    {
                                                                        fantasyGame.homeAway
                                                                    }
                                                                </span>

                                                                <strong>
                                                                    {
                                                                        fantasyGame.opponent
                                                                    }
                                                                </strong>

                                                                <small>
                                                                    {formatDate(
                                                                        fantasyGame.date
                                                                    )}
                                                                </small>
                                                            </div>

                                                            <p>
                                                                {getGameSummary(
                                                                    player,
                                                                    gameStats,
                                                                    fantasyGame
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="fantasy-player-modal__game-points">
                                                            <span>
                                                                PTS FANTASY
                                                            </span>

                                                            <strong>
                                                                {fantasyGame.fantasyPoints.toFixed(
                                                                    2
                                                                )}
                                                            </strong>
                                                        </div>
                                                    </article>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </section>
                        </main>
                    </>
                ) : null}
            </IonContent>
        </IonModal>
    );
};

export default FantasyPlayerModal;
